/**
 * Групи сторінки «Довідники» — і що в кожній.
 *
 * Групи, порядок і описи — `SPEC-007` §7.2. Порядок груп повторює бокове меню
 * Астерікса (CRM → Перевезення → …), далі наскрізні групи; усередині групи —
 * від загального до окремого. Тут лише довідники, які вже є: юрособи, типи
 * документів і шаблони листів додають їхні наряди (S3c, S3d, S8a) у свої групи.
 *
 * ⚠️ Опис — **призначення** довідника, а не приклади значень. Приклади
 * застарівають мовчки, щойно хтось відредагує сам довідник, а ще збігаються з
 * даними, які шукають e2e після кліку по картці: Playwright шукає текст
 * підрядком без урахування регістру, і перевірка «після переходу видно
 * значення» проходила б на описі картки, ще до переходу.
 *
 * ⚠️ Опис звіряйте з кодом, а не з уявленням про довідник: читайте сутність,
 * яка на довідник посилається, і фікстуру з його значеннями.
 *
 * Опис тримаємо в межах ~28 символів — стільки вміщує рядок картки на
 * ноутбучному екрані в три колонки. Довший перенесеться на другий рядок, але
 * вирівняна сітка з однорядковими описами читається помітно легше.
 *
 * ⚠️ Довідник, якого тут немає, не зникає — він потрапляє в групу «Інше». Але
 * вартовий «кожен довідник належить групі» падає на ньому одразу: без групи
 * нова книга опиняється в самому низу, де її ніхто не шукає.
 */
export interface ListGroup {
    key: string
    title: string
    /** Ключ ресурсу → опис під назвою картки */
    items: ReadonlyArray<readonly [string, string]>
}

export const LIST_GROUPS: readonly ListGroup[] = [
    {
        key: 'crm',
        title: 'CRM',
        items: [
            ['Setting.ListClientStatus', 'Стан картки клієнта'],
            ['Setting.ListCarrierStatus', 'Стан картки перевізника'],
            ['Setting.ListActivityStatus', 'Нова чи завершена справа'],
            ['Setting.ListActivityType', 'Вид справи в її картці'],
        ],
    },
    {
        key: 'transportation',
        title: 'Перевезення',
        items: [
            ['Setting.ListApplicationStatus', 'Етапи виконання заявки'],
            ['Setting.ListRequestStatus', 'Етапи запиту ціни'],
            ['Setting.ListTenderStatus', 'Етапи тендера'],
            ['Setting.ListApplicationLossReason', 'Чому заявку програно'],
            ['Setting.ListRequestLossReason', 'Чому запит програно'],
            ['Setting.ListTenderLossReason', 'Чому тендер програно'],
            ['Setting.ListBorderCrossing', 'Перехід кордону в заявці'],
        ],
    },
    {
        key: 'transport',
        title: 'Транспорт',
        items: [
            ['Setting.ListCarType', 'Транспорт перевізника'],
            ['Setting.ListCarOwnershipType', 'Підстава володіння авто'],
            ['Setting.ListCarParkSize', 'Розмір автопарку перевізника'],
        ],
    },
    {
        key: 'requisites',
        title: 'Реквізити й документи',
        items: [
            ['Setting.ListLegalEntity', 'Експедитор у документах'],
            ['Setting.ListFormTaxation', 'Оподаткування в реквізитах'],
            ['Setting.ListDocumentType', 'Шаблони друкованих форм'],
            ['Setting.ListLetterTemplate', 'Листи клієнтам, перевізникам'],
        ],
    },
    {
        key: 'general',
        title: 'Загальні',
        items: [
            ['Setting.ListCountry', 'Для перевізників і маршрутів'],
            ['Setting.ListCurrency', 'Валюти сум і курсів'],
            ['Setting.ListCurrencyRate', 'Курс валюти на дату'],
        ],
    },
]

/**
 * Порядок довідників — як на сторінці: за ним іде і горизонтальне меню над
 * кожним довідником (`TopMenu`), щоб людина бачила ту саму послідовність.
 */
export const LIST_ORDER: readonly string[] = LIST_GROUPS.flatMap((group) => group.items.map(([key]) => key))

/** Група для довідника, якого немає в переліку вище. */
export const FALLBACK_GROUP: Pick<ListGroup, 'key' | 'title'> = { key: 'other', title: 'Інше' }
